import React from 'react'
import Confirm from '../../components/confirm/confirm'
import DashboardMenu from '../../components/dashboardMenu/dashboardMenu'
import useApi, {CONTROLLER} from '../../hooks/useApi'
import {dateToString} from '../../utils/date'
import {prettyAmount} from '../../utils/amount'
import Spinner from '../../components/spinner/spinner'
import './orders.css'

const getStatusClassName = (status) => {
  const value = String(status || '').trim().toLowerCase();
  if (value === 'active') return 'dt-orders-status dt-orders-status--active';
  if (value === 'trial') return 'dt-orders-status dt-orders-status--trial';
  if (value === 'inactive') return 'dt-orders-status dt-orders-status--inactive';
  return 'dt-orders-status';
};

const Orders = () => {
  const [id, setId] = React.useState(null);
  const {
    loading: ordersLoading,
    payload: ordersPayload,
    execute: ordersExecute,
  } = useApi(CONTROLLER.ORDERS);
  const {
    loading: cancelLoading,
    execute: cancelExecute,
  } = useApi(CONTROLLER.CANCEL_SUBSCRIPTION);
  const [state, setState] = React.useState({
    show: false,
    title: "",
    message: "",
  });

  React.useEffect(() => {
    ordersExecute();
  }, [ordersExecute]);

  const cancelSubscription = (id) => {
    setId(id);
    setState({
      show: true,
      title: "Cancel Subscription",
      message: "Are you sure you want to cancel your subscription plan?",
    });
  }

  const handleClose = async () => {
    setState({
      show: false,
      title: "",
      message: ""
    });
  }

  const handleOk = async () => {
    setState({
      show: false,
      title: "",
      message: ""
    });
    const {subscription_id} = await cancelExecute({id});
    // --- facebook ---
    window.fbq && window.fbq('track', 'Cancellation', {subscription_id});
    // --- google ---
    window.gtag && window.gtag('event', 'cancel_subscription', {event_category: 'ecommerce', subscription_id});
    await ordersExecute();
  }

  if (ordersLoading) {
    return <Spinner/>
  }

  const orders = Array.isArray(ordersPayload?.data) ? ordersPayload.data : [];
  const activeOrders = orders.filter((item) => String(item?.Status || '').toLowerCase() === 'active');
  const cancelableOrders = orders.filter((item) => Boolean(item?.isCancelable));
  const nextRenewalDate = orders
    .map((item) => item?.RenewalDate)
    .filter(Boolean)
    .sort((a, b) => (new Date(a)).getTime() - (new Date(b)).getTime())[0];

  return (
    <section className="dashboardSec dt-orders-page">
      <div className="dashOrdSec dt-orders-wrap">
        <div className="container">
          <DashboardMenu/>
          <div className="row dt-orders-head-row">
            <div className="col-12">
              <h2 className="dt-orders-title"><span>Orders</span></h2>
              <p className="dt-orders-subtitle">
                Manage your plan, renewal timing, and subscription status in one place.
              </p>
            </div>
          </div>

          <div className="row dt-orders-kpis-row">
            <div className="col-12">
              <div className="dt-orders-kpis">
                <div className="dt-orders-kpi">
                  <div className="dt-orders-kpi-label">Total Orders</div>
                  <div className="dt-orders-kpi-value">{orders.length}</div>
                </div>
                <div className="dt-orders-kpi">
                  <div className="dt-orders-kpi-label">Active Plans</div>
                  <div className="dt-orders-kpi-value">{activeOrders.length}</div>
                </div>
                <div className="dt-orders-kpi">
                  <div className="dt-orders-kpi-label">Cancelable</div>
                  <div className="dt-orders-kpi-value">{cancelableOrders.length}</div>
                </div>
                <div className="dt-orders-kpi">
                  <div className="dt-orders-kpi-label">Next Renewal</div>
                  <div className="dt-orders-kpi-value dt-orders-kpi-value--small">
                    {nextRenewalDate ? dateToString(nextRenewalDate) : '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="dt-orders-card">
                <div className="dt-orders-card-head">
                  <div className="dt-orders-card-title">Billing History</div>
                  <div className="dt-orders-card-note">Manage recurring subscriptions from your billing history.</div>
                </div>
                <div className="orderTable dt-orders-table-wrap">
                  <table className="table dt-orders-table">
                  <thead>
                  <tr>
                    <th valign="middle">Date</th>
                    <th valign="middle">Order</th>
                    <th valign="middle">Plan</th>
                    <th valign="middle">Amount</th>
                    <th valign="middle">Status</th>
                    <th valign="middle">Expires</th>
                    <th valign="middle">Renewal Date</th>
                    <th valign="middle">Trial Expires</th>
                    <th valign="middle">&nbsp;</th>
                  </tr>
                  </thead>
                  <tbody>
                  {orders.map((item) => (
                    <tr key={item.OrderId}>
                      <td>
                        {dateToString(item.OrderDate)}
                      </td>
                      <td>
                        #{item.OrderId}
                      </td>
                      <td>
                        <span className="dt-orders-plan-pill">{item.Type}</span>
                      </td>
                      <td>
                        ${prettyAmount(item.Amount)}
                      </td>
                      <td valign="middle">
                        <span className={getStatusClassName(item.Status)}>
                          {String(item.Status || '').trim() || 'Unknown'}
                        </span>
                      </td>
                      <td>
                        {item.ExpirationDate && dateToString(item.ExpirationDate)}
                      </td>
                      <td>
                        {item.RenewalDate && dateToString(item.RenewalDate)}
                      </td>
                      <td>
                        {item.TrialExpirationDate && dateToString(item.TrialExpirationDate)}
                      </td>
                      <td>
                        {item.isCancelable !== null && (
                          <button
                            type="button"
                            className="dt-orders-cancel-btn"
                            onClick={() => cancelSubscription(item.OrderId)}
                            disabled={!item.isCancelable || cancelLoading}
                          >
                            {cancelLoading && id === item.OrderId ? 'Canceling…' : 'Cancel Subscription'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!orders.length && (
                    <tr>
                      <td colSpan={9} className="dt-orders-empty">No orders yet.</td>
                    </tr>
                  )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      <Confirm
        {...state}
        handleOk={handleOk}
        handleClose={handleClose}
      />
    </section>
  );
}

export default Orders;
